import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  cancelSubscription,
  getSubscription,
  renewSubscription,
} from 'src/common/actions/subscription';
import OkDialog from 'src/common/components/shared/dialog/OkDialog';
import { MONTHLY_SUBSCRIPTION_RENEW_SKU } from 'src/common/constants/experiments/skus';
import { CANCEL_SUBSCRIPTION_SUCCESS } from 'src/common/constants/localizations/subscription';
import connectWithExperiments from 'src/common/decorators/connectWithExperiments';
import asyncStatuses from 'src/common/utils/asyncStatuses';
import {
  currentSubscriptionStatus,
  premiumSubscriptionStatus,
} from 'src/common/utils/subscription/subscriptionStatus';
import vars from 'src/common/vars';
import { LocationAndLocalizationContext } from '../../../../providers/LocationAndLocalizationProvider';
import { selectSubscriptionStatus } from '../../../../selectors/me';
import { selectCurrentSubscription } from '../../../../selectors/subscription';
import getIsRenewalEligible from '../../../../utils/subscription/getIsRenewalEligible';
import RestorePurchase from '../RestorePurchase';
import CancelConfirmationDialog from './CancelConfirmationDialog';
import Active from './views/Active';
import Cancelled from './views/Cancelled';

export class WebSubscription extends Component {
  static propTypes = {
    premiumStatus: PropTypes.string.isRequired,
    subscriptionStatus: PropTypes.string,
    cancellationStatus: PropTypes.string,
    actions: PropTypes.object.isRequired,
    formattedPrice: PropTypes.string,
    formattedDiscountPrice: PropTypes.string,
    termEndDate: PropTypes.string,
    chargedThroughDate: PropTypes.string,
    serviceActivationDate: PropTypes.string,
    billingPeriod: PropTypes.string,
    products: PropTypes.object,
    hasBillingInfo: PropTypes.bool,
    isRenewalEligible: PropTypes.bool.isRequired,
    couponData: PropTypes.object,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.state = {
      showCancelConfirmationDialog: false,
      showCancelConfirmationSuccessDialog: false,
      monthlyRenewSku: this.props[MONTHLY_SUBSCRIPTION_RENEW_SKU] || vars.get('MONTHLY_RENEW_SKU'),
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.cancellationStatus !== prevProps.cancellationStatus &&
      this.props.cancellationStatus === asyncStatuses.fulfilled
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        showCancelConfirmationDialog: false,
        showCancelConfirmationSuccessDialog: true,
      });
    }
  }

  handleCancelSubscriptionButtonClicked = () => {
    this.setState({ showCancelConfirmationDialog: true });
  };

  handleCancelSuccessClose = () => {
    this.setState({ showCancelConfirmationSuccessDialog: false });
  };

  handleCancelConfirmationClose = () => {
    this.setState({ showCancelConfirmationDialog: false });
  };

  handleConfirmCancelButtonClicked = () => {
    this.props.actions.cancelSubscription(this.context.location);
  };

  render() {
    const {
      premiumStatus,
      subscriptionStatus,
      cancellationStatus,
      formattedPrice,
      formattedDiscountPrice,
      termEndDate,
      chargedThroughDate,
      serviceActivationDate,
      billingPeriod,
      hasBillingInfo,
      products,
      isRenewalEligible,
      couponData,
    } = this.props;
    const renewalDate = chargedThroughDate || serviceActivationDate;
    const { showCancelConfirmationDialog, showCancelConfirmationSuccessDialog } = this.state;
    const { getLocalizedText } = this.context;

    return (
      <div>
        {subscriptionStatus === currentSubscriptionStatus.active && (
          <Active
            handleCancelSubscriptionButtonClicked={this.handleCancelSubscriptionButtonClicked}
            renewalDate={renewalDate}
            formattedPrice={formattedPrice}
            formattedDiscountPrice={formattedDiscountPrice}
            billingPeriod={billingPeriod}
            hasBillingInfo={hasBillingInfo}
            couponData={couponData}
          />
        )}
        {isRenewalEligible && (
          <Cancelled
            termEndDate={termEndDate}
            premiumStatus={premiumStatus}
            renewProduct={products[this.state.monthlyRenewSku]}
          />
        )}
        {premiumStatus !== premiumSubscriptionStatus.active && !isRenewalEligible && (
          <RestorePurchase />
        )}
        <CancelConfirmationDialog
          shouldShow={showCancelConfirmationDialog}
          handleClose={this.handleCancelConfirmationClose}
          confirmAction={this.handleConfirmCancelButtonClicked}
          chargedThroughDate={renewalDate}
          cancellationStatus={cancellationStatus}
        />
        <OkDialog
          shouldShow={showCancelConfirmationSuccessDialog}
          handleClose={this.handleCancelSuccessClose}
          content={getLocalizedText(CANCEL_SUBSCRIPTION_SUCCESS)}
          dataTestId="cancelSuccessDialog"
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { subscription, products } = state;
  const currentSubscription = selectCurrentSubscription(state);
  const {
    status,
    formattedPrice,
    termEndDate,
    chargedThroughDate,
    serviceActivationDate,
    billingPeriod,
    hasBillingInfo,
    newPlan,
    couponData,
  } = currentSubscription;

  return {
    isRenewalEligible: getIsRenewalEligible(state),
    premiumStatus: selectSubscriptionStatus(state),
    subscriptionStatus: status,
    cancellationStatus: subscription.cancellationStatus,
    formattedPrice,
    formattedDiscountPrice: newPlan?.formattedDiscountedPrice,
    termEndDate,
    chargedThroughDate,
    serviceActivationDate,
    billingPeriod,
    products,
    hasBillingInfo,
    couponData,
  };
}

function mapDispatchToProps(dispatch) {
  const actions = {
    getSubscription,
    renewSubscription,
    cancelSubscription,
  };

  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  connectWithExperiments([MONTHLY_SUBSCRIPTION_RENEW_SKU]),
)(WebSubscription);
